"use client"

/**
 * HouseAd — self-promo interstitial shown when no network ad is available.
 *
 * Pulls a real, admin-configured ad from /api/ads/serve (the house_ads table)
 * instead of a single hardcoded creative. Previously this always rendered one
 * baked-in image ("glym_noway.webp") regardless of what admins added, edited,
 * or toggled in the House Ads manager — every other house ad they configured
 * was invisible everywhere in the app.
 *
 * Two render modes, chosen per-ad by whether cta_text is set:
 *  - Text mode: title/subtitle overlay + a real CTA button, over the ad's own
 *    background_gradient (matches the original look for ads that want it).
 *  - Image-only mode: the media fills the frame, the whole thing is one click
 *    target, no text drawn over it at all — this is what "some should just be
 *    an image, no text" needs, and it was structurally impossible before
 *    because the interstitial never read title/cta_text as optional.
 *
 * Falls back to the original static creative only if the fetch itself fails
 * (offline, API error) — not as a first-class alternative to real ads.
 */

import { useEffect, useState, useRef } from "react"
import Link from "next/link"
import { Megaphone, } from "@/lib/icons"
import { useAdStore, trackImpression, trackClick } from "@/lib/ad-store"
import { useTranslations } from "next-intl"
import { useModalContent } from "@/lib/modal-content"

interface HouseAdProps {
  onClose?: () => void
}

interface ServedHouseAd {
  id: string
  campaign_id: string
  title: string | null
  subtitle: string | null
  media_url: string | null
  image_url?: string | null
  media_type: "image" | "gif" | "video"
  click_url: string
  cta_text: string | null
  background_gradient: string | null
  once_per_session?: boolean
}

const FALLBACK_IMAGE_ASPECT_RATIO = "1008 / 1792"
const SEEN_SESSION_KEY = "goonru_house_ads_seen_this_session"
const CURSOR_KEY = "goonru_house_interstitial_cursor"

function getSeenThisSession(): Set<string> {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SEEN_SESSION_KEY) ?? "[]"))
  } catch {
    return new Set()
  }
}

function markSeenThisSession(id: string) {
  try {
    const seen = getSeenThisSession()
    seen.add(id)
    sessionStorage.setItem(SEEN_SESSION_KEY, JSON.stringify([...seen]))
  } catch { /* best-effort */ }
}

export function HouseAd({ onClose }: HouseAdProps) {
  const t = useTranslations("houseAd")
  // Art and button text of the built-in main ad, editable in admin.
  const mainAd = useModalContent("house_interstitial_main")
  const [ad, setAd] = useState<ServedHouseAd | null | undefined>(undefined)
  const sessionId = useAdStore((s) => s.sessionId)
  const impressionTracked = useRef(false)

  // House ads reported nothing at all before this — neither impressions nor
  // clicks — so the admin House Ads manager had no numbers behind it.
  // Attribution goes through house_ad_id (a text id) rather than campaign_id,
  // per migration 20261114000000; the uuid guard on the route drops it
  // otherwise. Mount-based rather than viewport-based on purpose: this only
  // renders inside the fullscreen interstitial, so it is by definition on
  // screen when it renders.
  useEffect(() => {
    if (!ad || impressionTracked.current) return
    impressionTracked.current = true
    trackImpression({
      campaign_id: ad.campaign_id,
      house_ad_id: ad.id,
      slot_id: "interstitial_house",
      session_id: sessionId,
      page_url: typeof window !== "undefined" ? window.location.href : "",
      device_type: typeof window !== "undefined" && window.innerWidth < 768 ? "mobile" : "desktop",
      viewable: true,
      view_duration_ms: 0,
    })
  }, [ad, sessionId])

  useEffect(() => {
    // Ask for a small batch, not just one — an ad flagged once_per_session
    // that's already been shown this session needs a fallback candidate to
    // fall through to instead of just not appearing at all. Non-flagged ads
    // (the "funny" recurring ones) are never filtered out here.
    //
    // `ignore` guards against a real, reported bug: "the house interstitial
    // flickers between two different ads." This effect had no cancellation
    // guard, so React Strict Mode's dev-only double-invoke (mount → cleanup
    // → mount again, on by default and not opted out of in next.config)
    // fired the fetch twice on every mount -- whichever response landed
    // second silently overwrote the first via setAd, which is exactly a
    // visible ad swap right after the first one painted. Without the guard
    // this would also bite a genuine remount in production, not just Strict
    // Mode in dev, so it's a real fix either way, not just a dev artifact.
    let ignore = false
    // interstitial=1: only the ads the admin flagged for the full-screen
    // slot, in the admin's chosen order. `cursor` is this browser's position
    // in that rotation, so sequential mode walks the list instead of
    // repeating the first ad every time.
    let cursor = 0
    try { cursor = parseInt(localStorage.getItem(CURSOR_KEY) ?? "0") || 0 } catch { /* best-effort */ }
    fetch(`/api/ads/serve?count=5&interstitial=1&cursor=${cursor}`)
      .then((r) => r.json())
      .then((ads: ServedHouseAd[]) => {
        if (ignore) return
        if (!Array.isArray(ads) || ads.length === 0) { setAd(null); return }
        const seen = getSeenThisSession()
        const pickIndex = ads.findIndex((a) => !a.once_per_session || !seen.has(a.id))
        const pick = pickIndex >= 0 ? ads[pickIndex] : null
        if (pick?.once_per_session) markSeenThisSession(pick.id)
        if (pick) {
          try { localStorage.setItem(CURSOR_KEY, String(cursor + pickIndex + 1)) } catch { /* best-effort */ }
        }
        setAd(pick)
      })
      .catch(() => { if (!ignore) setAd(null) })

    return () => { ignore = true }
  }, [])

  // Still loading: hold the space with a blank box. Showing the static
  // fallback here made a different ad flash up first and then get replaced
  // by the real one once the fetch landed, which read as the interstitial
  // "switching between ads".
  if (ad === undefined || (ad === null && !mainAd.ready)) {
    return <div className="w-full rounded-2xl" style={{ aspectRatio: FALLBACK_IMAGE_ASPECT_RATIO }} />
  }

  // Fetch finished and nothing is flagged for the interstitial (or it failed)
  // — show the original static main creative rather than an empty interstitial.
  if (!ad) {
    return (
      <div className="relative w-full overflow-hidden rounded-2xl select-none" style={{ aspectRatio: FALLBACK_IMAGE_ASPECT_RATIO }}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={mainAd.art("hero")}
          alt={t("youReStillWatchingAdsSupportThePlatform")}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <Link
          href="/subscribe"
          onClick={onClose}
          className="absolute left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-6 py-3 rounded-xl font-black text-sm tracking-wide whitespace-nowrap transition-all
            bg-gradient-to-r from-violet-600 to-purple-600 text-white
            hover:from-violet-500 hover:to-purple-500
            shadow-lg shadow-purple-900/60"
          style={{ top: "78%" }}
        >
          <Megaphone className="w-4 h-4" />
          {mainAd.text("cta", t("removeAdsFrom499Mo"))}
        </Link>
      </div>
    )
  }

  const mediaUrl = ad.media_url || ad.image_url || ""
  const hasText = !!(ad.title || ad.subtitle || ad.cta_text)

  // In normal document flow (not absolutely positioned over a fixed-aspect
  // parent), so the box sizes itself to the media's own natural dimensions
  // — capped so it never overflows the modal — instead of stretching or
  // cropping the image to fit a fixed 1008/1792 box that only matched the
  // original hardcoded creative.
  const media = ad.media_type === "video" ? (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video src={mediaUrl} autoPlay muted loop playsInline className="block w-full h-auto max-h-[80vh] object-contain rounded-2xl" />
  ) : (
    // eslint-disable-next-line @next/next/no-img-element
    <img src={mediaUrl} alt={ad.title || "Advertisement"} className="block w-full h-auto max-h-[80vh] object-contain rounded-2xl" />
  )

  // Image-only mode: no overlay text at all, whole card is the click target.
  if (!hasText) {
    return (
      <Link
        href={ad.click_url}
        onClick={() => {
          trackClick(ad.id, ad.campaign_id, ad.id)
          onClose?.()
        }}
        target={ad.click_url.startsWith("http") ? "_blank" : undefined}
        rel={ad.click_url.startsWith("http") ? "noopener noreferrer" : undefined}
        className="block select-none"
      >
        {media}
      </Link>
    )
  }

  // Text mode: title/subtitle overlay + CTA button. The whole card is the
  // click target (like the in-grid house ads already are) — the CTA button
  // is styling, not the only clickable part, so clicking the image/title
  // area works exactly the same as clicking the button.
  return (
    <Link
      href={ad.click_url}
      onClick={onClose}
      target={ad.click_url.startsWith("http") ? "_blank" : undefined}
      rel={ad.click_url.startsWith("http") ? "noopener noreferrer" : undefined}
      className="relative block select-none rounded-2xl overflow-hidden"
      style={{ background: ad.background_gradient ?? undefined }}
    >
      {media}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center p-6 space-y-3 text-center">
        {ad.title && <h3 className="text-xl font-black text-white leading-tight">{ad.title}</h3>}
        {ad.subtitle && <p className="text-sm text-white/80">{ad.subtitle}</p>}
        {ad.cta_text && (
          <span
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-black text-sm tracking-wide whitespace-nowrap
              bg-gradient-to-r from-violet-600 to-purple-600 text-white
              shadow-lg shadow-purple-900/60"
          >
            <Megaphone className="w-4 h-4" />
            {ad.cta_text}
          </span>
        )}
      </div>
    </Link>
  )
}
